import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react';
import {
  AmbientLight,
  Color,
  type ColorRepresentation,
  type Object3D,
  Scene as ThreeScene,
} from 'three';
import { isEmpty } from '@witmetrics/utils/misc';
import type {
  PositionPreset,
  TrackballControls,
  VectorParams,
} from '@/utils/scans/trackballControls';
import { useMountEffect } from '@/hooks/useMountEffect';
import {
  getDirectionalLights,
  getNonObjects,
  getObjects,
  getObjectsZoom,
} from './utils';

export interface SceneProviderProps extends PropsWithChildren {
  backgroundColor?: ColorRepresentation;
}

type SceneValue = {
  getScene: () => ThreeScene;
  getControls: () => TrackballControls | undefined;
  setControls: (controls: TrackballControls) => void;
  updateControls: () => void;
  rotateControls: (rotation: VectorParams | PositionPreset) => void;
  addToScene: (objects: Object3D[], clearExisting?: boolean) => void;
  removeFromScene: (uuid: string) => void;
  removeFromSceneByFileId: (fileId: number) => void;
  clearScene: () => void;
  resetCamera: () => void;
  zoomToObjects: (objects: Object3D[]) => void;
};

const SceneContext = createContext<SceneValue | undefined>(undefined);

export default function SceneProvider({
  backgroundColor = 0xf3f5f7 /*bg-grey-2*/,
  children,
}: SceneProviderProps) {
  const sceneRef = useRef(new ThreeScene());
  const controlsRef = useRef<TrackballControls>();

  useMountEffect(() => {
    sceneRef.current.background = new Color(backgroundColor);
    window.threeScene = sceneRef.current;
    // addToScene([new AmbientLight(0xffffff, 0.6)]);
    addToScene([new AmbientLight(0xffffff, 1)]);

    return () => {
      sceneRef.current.children = [];
      controlsRef.current?.dispose();
      window.threeScene = undefined;
      window.threeControls = undefined;
    };
  });

  const getScene = () => sceneRef.current;

  const getControls = () => controlsRef.current;

  const setControls = (controls: TrackballControls) => {
    controlsRef.current = controls;
    window.threeControls = controlsRef.current;
    sceneRef.current.children = sceneRef.current.children.filter(
      (o) => !o.type.toLowerCase().includes('camera')
    );
    const hasLights = controlsRef.current.object.children.length > 0;
    if (!hasLights) controlsRef.current.object.add(...getDirectionalLights());
    sceneRef.current.add(controlsRef.current.object);
  };

  const updateControls = () => controlsRef.current?.update();

  const rotateControls = (rotation: VectorParams | PositionPreset) => {
    if (!controlsRef.current) {
      return console.warn('TrackballControls not found');
    }
    controlsRef.current.setRotation(rotation);
  };

  const addToScene = (objects: Object3D[], clearExisting?: boolean) => {
    const existingObjects = getObjects(sceneRef.current.children);
    const newObjects = getObjects(objects);
    if (clearExisting) clearScene();
    sceneRef.current.add(...objects);
    if (!controlsRef.current) return;
    if (isEmpty(existingObjects) && !isEmpty(newObjects)) {
      zoomToObjects(newObjects);
    }
  };

  const removeFromScene = (uuid: string) => {
    sceneRef.current.children = sceneRef.current.children.filter(
      (c) => c.uuid !== uuid
    );
  };

  const removeFromSceneByFileId = (fileId: number) => {
    sceneRef.current.children = sceneRef.current.children.filter(
      (c) => c.userData.fileId !== fileId
    );
  };

  const clearScene = () => {
    sceneRef.current.children = getNonObjects(sceneRef.current.children);
  };

  const resetCamera = () => {
    controlsRef.current?.reset();
  };

  const zoomToObjects = (objects: Object3D[]) => {
    if (!controlsRef.current) throw Error('controlsRef.current is undefined');
    const { distance, center, direction } = getObjectsZoom(
      objects,
      controlsRef.current
    );
    controlsRef.current.maxDistance = distance * 10;
    controlsRef.current.target.copy(center);
    controlsRef.current.object.near = distance / 100;
    controlsRef.current.object.far = distance * 100;
    controlsRef.current.object.updateProjectionMatrix();
    controlsRef.current.object.position
      .copy(controlsRef.current!.target)
      .sub(direction);
    controlsRef.current.update();
    controlsRef.current.setDefaults();
  };

  const contextValue = {
    getScene,
    getControls,
    setControls,
    rotateControls,
    updateControls,
    addToScene,
    removeFromScene,
    removeFromSceneByFileId,
    clearScene,
    resetCamera,
    zoomToObjects,
  };

  return (
    <SceneContext.Provider value={contextValue}>
      {children}
    </SceneContext.Provider>
  );
}

export function useScene() {
  const context = useContext(SceneContext);
  if (!context) {
    throw Error('useScene must be used within a SceneProvider');
  }
  return context;
}
